import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { api, ApiError } from '@/client/lib/api';
import { Markdown } from '@/client/components/cms/Markdown';
import { Seo } from '@/client/components/Seo';
import { Button } from '@/client/components/ui/button';
import { isLocale, type Locale } from '@/client/i18n';

type CmsPagePayload = {
  slug: string;
  locale: Locale;
  title: string;
  body: string;
  metaTitle: string | null;
  metaDescription: string | null;
  updatedAt: number;
};

// CMS body is split into two halves on the marker "<!-- photo:middle -->".
// If the marker is absent, the entire body renders below the hero and no
// secondary photo is shown. Owner controls the split point from admin.
const PHOTO_MARKER = '<!-- photo:middle -->';

export function AboutDajana() {
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ['cms', 'o-dajane', locale],
    queryFn: () => api<CmsPagePayload>(`/api/v1/cms/o-dajane?locale=${locale}`),
    retry: false,
  });

  if (query.isLoading) {
    return (
      <div className="px-6 py-32 text-center font-sans text-body text-text-secondary">
        <div className="inline-flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <div className="relative">
          <Seo
            title={t('errors.not_found_title')}
            description={t('seo.default_description')}
            locale={locale}
            index={false}
          />
          <section className="mx-auto max-w-xl px-5 sm:px-6 lg:px-8 py-16 sm:py-24">
            <div className="dr-card-strong p-10 sm:p-14 text-center animate-fade-up">
              <p className="dr-eyebrow">404</p>
              <h1 className="mt-3 font-display text-h2 text-primary-dark">
                {t('errors.not_found_title')}
              </h1>
              <Link to={`/${locale}`} className="inline-block mt-8">
                <Button>
                  <ArrowLeft size={16} />
                  {t('errors.back_home')}
                </Button>
              </Link>
            </div>
          </section>
        </div>
      );
    }
    return (
      <div className="px-6 py-32 text-center font-sans text-body text-error">
        {t('errors.generic')}
      </div>
    );
  }

  if (!query.data) return null;

  const bodyParts = query.data.body.split(PHOTO_MARKER);
  const bodyAbove = bodyParts[0] ?? '';
  const bodyBelow = bodyParts.length > 1 ? bodyParts.slice(1).join(PHOTO_MARKER) : null;

  return (
    <article className="relative">
      <Seo
        title={query.data.metaTitle ?? query.data.title}
        description={query.data.metaDescription ?? t('seo.default_description')}
        locale={locale}
      />

      <div
        className="absolute top-10 -left-32 w-[26rem] h-[26rem] rounded-full pointer-events-none animate-float-orb"
        style={{
          background: 'radial-gradient(circle, rgba(193,167,226,0.35) 0%, transparent 65%)',
          filter: 'blur(40px)',
        }}
        aria-hidden
      />

      {/* BLOK 1 — Hero with photo */}
      <section className="relative mx-auto max-w-6xl px-5 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-12">
        <div className="grid gap-10 lg:gap-16 lg:grid-cols-2 items-center">
          <div className="order-2 lg:order-1">
            <p className="dr-eyebrow animate-fade-up">
              {t('cms.eyebrow', { defaultValue: 'Akadémia' })}
            </p>
            <h1
              className="mt-3 font-display text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight text-primary-dark animate-fade-up"
              style={{ animationDelay: '70ms' }}
            >
              {query.data.title}
            </h1>
          </div>
          <div className="order-1 lg:order-2 animate-fade-up" style={{ animationDelay: '140ms' }}>
            <img
              src="/photos/DR_Academy_Dajana_o_mne_1.jpg"
              alt={
                locale === 'cs'
                  ? 'Dajana Varga Rodriguez, zakladatelka značky Dajana Rodriguez'
                  : 'Dajana Varga Rodriguez, founderka značky Dajana Rodriguez'
              }
              loading="eager"
              decoding="async"
              className="w-full h-auto rounded-2xl object-cover aspect-[4/5] shadow-glass"
            />
          </div>
        </div>
      </section>

      {/* BLOK 2 — first half of CMS content */}
      <section className="relative mx-auto max-w-3xl px-5 sm:px-6 lg:px-8 pb-16">
        <div
          className="dr-card-static p-7 sm:p-10 lg:p-12 animate-fade-up"
          style={{ animationDelay: '210ms' }}
        >
          <Markdown source={bodyAbove} />
        </div>
      </section>

      {bodyBelow !== null && (
        <>
          {/* BLOK 3 — secondary photo */}
          <section className="relative mx-auto max-w-5xl px-5 sm:px-6 lg:px-8 pb-16">
            <img
              src="/photos/DR_Academy_Dajana_o_mne_2.jpg"
              alt={
                locale === 'cs'
                  ? 'Dajana v showroomu značky Dajana Rodriguez'
                  : 'Dajana v showroome značky Dajana Rodriguez'
              }
              loading="lazy"
              decoding="async"
              className="w-full h-auto rounded-2xl object-cover aspect-[16/10] shadow-glass"
            />
          </section>

          {/* BLOK 4 — remaining CMS content */}
          <section className="relative mx-auto max-w-3xl px-5 sm:px-6 lg:px-8 pb-20">
            <div className="dr-card-static p-7 sm:p-10 lg:p-12">
              <Markdown source={bodyBelow} />
            </div>
          </section>
        </>
      )}
    </article>
  );
}
